// components/layout/NavMenu/hooks/useNavActions.ts
import { useState, useCallback, Dispatch, SetStateAction } from 'react';
import { ref, update, remove } from 'firebase/database';
import { db } from '../../../../config/firebase';
import type { UserData } from '../../../../context/AuthContext';
import { logger } from '../../../../utils/logger';
import { fetchTournamentMetadataOnce } from '../../../../hooks/useTournamentMetadata';

export interface NavActionsInput {
  user: UserData | null;
  onBreak: boolean;
}

export interface NavActionsResult {
  // Drawer state
  drawerOpen: boolean;
  setDrawerOpen: Dispatch<SetStateAction<boolean>>;
  toggleDrawer: () => void;
  closeDrawer: () => void;

  // Menu anchors
  roundsAnchor: HTMLElement | null;
  setRoundsAnchor: Dispatch<SetStateAction<HTMLElement | null>>;

  // Section expansion
  tournamentExpanded: boolean;
  setTournamentExpanded: Dispatch<SetStateAction<boolean>>;
  appealsExpanded: boolean;
  setAppealsExpanded: Dispatch<SetStateAction<boolean>>;
  managementExpanded: boolean;
  setManagementExpanded: Dispatch<SetStateAction<boolean>>;
  streamExpanded: boolean;
  setStreamExpanded: Dispatch<SetStateAction<boolean>>;

  // Actions
  toggleBreak: () => Promise<void>;
}

/**
 * Hook for managing navigation actions
 */
export function useNavActions({ user, onBreak }: NavActionsInput): NavActionsResult {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [roundsAnchor, setRoundsAnchor] = useState<HTMLElement | null>(null);

  // Collapsed section states
  const [tournamentExpanded, setTournamentExpanded] = useState(false);
  const [appealsExpanded, setAppealsExpanded] = useState(false);
  const [managementExpanded, setManagementExpanded] = useState(false);
  const [streamExpanded, setStreamExpanded] = useState(false);

  const toggleDrawer = useCallback(() => {
    setDrawerOpen(prev => !prev);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // Toggle break status and cascade acting-team-lead assignments.
  // For Team Lead / Head Judge we scan their active tournaments
  // and (promote | demote) the assistant on each floor team they
  // run. Shared metadata accessor handles the orphan + active
  // status filter.
  const toggleBreak = useCallback(async () => {
    if (!user?.uid) return;

    const newBreakStatus = !onBreak;

    await update(ref(db, `users/${user.uid}`), { onBreak: newBreakStatus });

    if (user.role === 'Team Lead' || user.role === 'Head Judge') {
      try {
        const { tournaments } = await fetchTournamentMetadataOnce();
        for (const m of tournaments) {
          const floorTeams = (m.floorTeams as Record<string, { teamLeadId?: string; assistantId?: string }> | undefined) || {};
          for (const [teamLetter, team] of Object.entries(floorTeams)) {
            if (team.teamLeadId === user.uid && team.assistantId) {
              if (newBreakStatus) {
                await update(ref(db, `users/${team.assistantId}/actingTeamLeadFor`), {
                  [m.id]: teamLetter,
                });
              } else {
                await remove(ref(db, `users/${team.assistantId}/actingTeamLeadFor/${m.id}`));
              }
            }
          }
        }
      } catch (err) {
        logger.error('Error updating acting team lead status:', err);
      }
    }
  }, [user?.uid, user?.role, onBreak]);

  return {
    // Drawer state
    drawerOpen,
    setDrawerOpen,
    toggleDrawer,
    closeDrawer,

    // Menu anchors
    roundsAnchor,
    setRoundsAnchor,

    // Section expansion
    tournamentExpanded,
    setTournamentExpanded,
    appealsExpanded,
    setAppealsExpanded,
    managementExpanded,
    setManagementExpanded,
    streamExpanded,
    setStreamExpanded,

    // Actions
    toggleBreak,
  };
}

export default useNavActions;
